import CorpusData from '@/data/CorpusData';
import type Metadata from '@/data/Metadata';
import type { MetadataFormat } from '@/data/Metadata';
import type { CountryCode } from '@/data/Localized';
import MetadataParser from './MetadataParser';

type CorpusTitleKey = `title:${CountryCode}`;

type CorpusTitleMap = Record<CorpusTitleKey, string>;

type CorpusTextKey = `text:${CountryCode}`;

type CorpusTextMap = Record<CorpusTextKey, string>;

type CorpusMottoKey = `motto:${CountryCode}`;

type CorpusMottoMap = Record<CorpusMottoKey, string>;

type CorpusRefKey = `ref:${string}`;

type CorpusRefMap = Record<CorpusRefKey, string>;

export interface CorpusDataFormat
  extends CorpusTitleMap, CorpusTextMap, CorpusMottoMap, CorpusRefMap, MetadataFormat {
  id: string,
  path: string,
  title: string,
  shorttitle?: string,
  genre?: string,
  availability?: string,
  image?: string,
  imageCard?: string,
  'quality:corpus'?: number,
  showcase?: Array<string>,
  opus?: string,
  motto?: string,
  columns?: string,
}

function checkString(val: unknown): string {
  if (typeof val === 'number') return val.toString();
  if (typeof val !== 'string') {
    throw new Error(`Corrupted Corpus: ${val} is not a string`);
  }
  return val;
}

export default class CorpusParser extends CorpusData {
  static fromFormat(
    format: CorpusDataFormat,
  ): CorpusData {
    let id = '';
    let path = '';
    try {
      id = checkString(format.id);
      path = checkString(format.path);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    const title = checkString(format.title || '');
    const shorttitle = checkString(format.shorttitle || '');
    const motto = checkString(format.motto || '');
    const columns = checkString(format.columns || '');

    const localizedShortTitles: Record<CountryCode, string> = {};
    const localizedTitles: Record<CountryCode, string> = {};
    const localizedTexts: Record<CountryCode, string> = {};
    const localizedMottos: Record<CountryCode, string> = {};
    const localizedAvailabilities: Record<CountryCode, string> = {};

    Object.keys(format).forEach((k) => {
      const splitKey = k.split(':');
      if (splitKey.length === 2) {
        const lang = splitKey[1].split('-')[0];

        if (splitKey[0] === 'shorttitle') {
          const localeShortTitle = checkString((format as unknown as Record<string, string>)[k]);
          localizedShortTitles[lang] = localeShortTitle;
        }
        if (splitKey[0] === 'title') {
          const localeTitle = checkString((format as unknown as Record<string, string>)[k]);
          localizedTitles[lang] = localeTitle;
        }
        if (splitKey[0] === 'text') {
          const localeText = checkString((format as unknown as Record<string, string>)[k]);
          localizedTexts[lang] = localeText;
        }
        if (splitKey[0] === 'motto') {
          const localeMotto = checkString((format as unknown as Record<string, string>)[k]);
          localizedMottos[lang] = localeMotto;
        }
        if (splitKey[0] === 'availability') {
          const localeAvailability = checkString((format as unknown as Record<string, string>)[k]);
          localizedAvailabilities[lang] = localeAvailability;
        }
      }
    });

    const genre = checkString(format.genre || '');
    const availability = checkString(format.availability || '');
    const quality = format['quality:corpus'] ?? 0;
    const image = checkString(format.image || '');
    const imageCard = checkString((format as unknown as Record<string, string>)['image:card'] || image);
    const showcase = format.showcase ? format.showcase[Math.round(Math.random() * (format.showcase.length - 1))] : '';

    const opus = checkString(format.opus || '');

    const metadata : Metadata = MetadataParser.fromFormat(format);

    return new CorpusData({
      id,
      path,
      title,
      localizedTitles,
      shorttitle,
      localizedShortTitles,
      localizedTexts,
      motto,
      localizedMottos,
      genre,
      availability,
      localizedAvailabilities,
      quality,
      image,
      imageCard,
      showcase,
      opus,
      columns,
      metadata,
    });
  }
}
